"use client";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useInView,
  useReducedMotion,
  type MotionValue,
} from "framer-motion";
import { Icon24Rocket } from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { cn } from "@/lib/utils";
import { BlurFade } from "@/components/magicui/blur-fade";
import { MaskReveal } from "@/components/motion/mask-reveal";
import { PhoneFrame } from "./phone-frame";
import { PhoneScreen } from "./flow-screens";
import {
  FEATURES,
  STEPS,
  TOTAL_STEPS,
  FEATURE_START_INDEX,
  type FlowFeature,
} from "./flow-data";

/** Vertical scroll budget per shot, in viewport heights. */
const STEP_VH = 58;

/* -------------------------------------------------------------------------- */
/*  Intro                                                                      */
/* -------------------------------------------------------------------------- */

function FlowIntro() {
  return (
    <div className="container mx-auto px-4 pt-24 text-center md:pt-28">
      <BlurFade inView delay={0.05}>
        <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-800">
          <Icon24Rocket className="h-4 w-4 text-brand-600" />
          {Language.getKey("landing_flow_badge")}
        </span>
      </BlurFade>
      <h2 className="mt-6 font-heading text-3xl font-bold text-brand-900 md:text-5xl">
        <MaskReveal>
          {Language.getKey("landing_flow_title_prefix")}
          <span className="text-brand-500">
            {Language.getKey("landing_flow_title_highlight")}
          </span>
        </MaskReveal>
      </h2>
      <BlurFade inView delay={0.18}>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
          {Language.getKey("landing_flow_subtitle")}
        </p>
      </BlurFade>
      <BlurFade inView delay={0.24}>
        <div className="mt-7 hidden items-center justify-center gap-2 text-sm font-medium text-brand-500 lg:flex">
          {Language.getKey("landing_flow_keep_scrolling")}
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10 4v12M5 11l5 5 5-5" />
            </svg>
          </motion.span>
        </div>
      </BlurFade>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Narrative rail (desktop)                                                   */
/* -------------------------------------------------------------------------- */

function FeatureRow({
  feature,
  featureIndex,
  active,
  step,
}: {
  feature: FlowFeature;
  featureIndex: number;
  active: boolean;
  step: number;
}) {
  const startIndex = FEATURE_START_INDEX[featureIndex];
  return (
    <div
      className={cn(
        "transition-opacity duration-500",
        active ? "opacity-100" : "opacity-40",
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-all duration-500",
            active
              ? "border-transparent bg-gradient-to-br from-brand-900 to-brand-500 text-white shadow-lg shadow-brand-500/30"
              : "border-brand-200 bg-white text-brand-500",
          )}
        >
          <feature.icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-500">
            {Language.getKey(feature.eyebrowKey)}
          </div>
          <div className="font-heading text-xl font-bold text-brand-900">
            {Language.getKey(feature.titleKey)}
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {active ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden pl-14"
          >
            <p className="pt-2 text-sm text-muted-foreground">
              {Language.getKey(feature.descriptionKey)}
            </p>
            <ul className="mt-3 space-y-2">
              {feature.actions.map((a, ai) => {
                const gi = startIndex + ai;
                const state =
                  step > gi ? "done" : step === gi ? "active" : "todo";
                return (
                  <li
                    key={a.labelKey}
                    className="flex items-center gap-2.5 text-sm"
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-full text-[9px] transition-all",
                        state === "done" && "bg-brand-900 text-white",
                        state === "active" &&
                          "bg-brand-500 text-white ring-4 ring-brand-500/15",
                        state === "todo" &&
                          "border border-brand-300 text-transparent",
                      )}
                    >
                      ✓
                    </span>
                    <span
                      className={cn(
                        "transition-colors",
                        state === "active" && "font-semibold text-brand-900",
                        state === "done" && "text-brand-600",
                        state === "todo" && "text-brand-400",
                      )}
                    >
                      {Language.getKey(a.labelKey)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function NarrativeRail({
  step,
  progress,
}: {
  step: number;
  progress: MotionValue<number>;
}) {
  const activeFeature = STEPS[step].featureIndex;
  return (
    <div className="relative">
      {/* Progress spine */}
      <div className="absolute bottom-2 left-0 top-2 w-px bg-brand-200">
        <motion.div
          style={{ scaleY: progress }}
          className="absolute inset-0 origin-top bg-gradient-to-b from-brand-900 to-brand-500"
        />
      </div>
      <div className="space-y-8 pl-8">
        {FEATURES.map((feature, fi) => (
          <FeatureRow
            key={feature.key}
            feature={feature}
            featureIndex={fi}
            active={fi === activeFeature}
            step={step}
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Phone stage (desktop)                                                      */
/* -------------------------------------------------------------------------- */

function CaptionPill({ index, label }: { index: number; label: string }) {
  return (
    <div className="absolute -top-12 left-1/2 z-20 -translate-x-1/2">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="flex items-center gap-2 whitespace-nowrap rounded-full border border-brand-200 bg-white/90 px-4 py-2 text-sm font-semibold text-brand-800 shadow-sm backdrop-blur"
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-900 text-[10px] text-white">
            {index + 1}
          </span>
          {label}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function PhoneHalo({ glow, featureKey }: { glow: string; featureKey: string }) {
  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={featureKey}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.6 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.7 }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-[440px] w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[100px]"
        style={{
          background: `radial-gradient(circle, ${glow}, transparent 70%)`,
        }}
      />
    </AnimatePresence>
  );
}

function FlowStage({
  step,
  progress,
}: {
  step: number;
  progress: MotionValue<number>;
}) {
  const { feature, action } = STEPS[step];
  const phoneY = useTransform(progress, [0, 1], [12, -12]);

  return (
    <div className="relative flex items-center justify-center">
      <PhoneHalo glow={feature.glow} featureKey={feature.key} />
      <motion.div style={{ y: phoneY }} className="relative">
        <CaptionPill index={step} label={Language.getKey(action.labelKey)} />
        <PhoneFrame>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28 }}
              className="absolute inset-0"
            >
              <PhoneScreen screen={action.screen} />
            </motion.div>
          </AnimatePresence>
        </PhoneFrame>
      </motion.div>
    </div>
  );
}

function DesktopFlow({ className }: { className?: string }) {
  const reduce = useReducedMotion();
  const outerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: outerRef,
    offset: ["start start", "end end"],
  });
  const [step, setStep] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const i = Math.min(
      TOTAL_STEPS - 1,
      Math.max(0, Math.floor(v * TOTAL_STEPS)),
    );
    setStep((prev) => (prev === i ? prev : i));
  });

  if (reduce) return <StackedFlow className={className} cycle={false} />;

  return (
    <div
      ref={outerRef}
      className={cn("relative", className)}
      style={{ height: `${TOTAL_STEPS * STEP_VH}vh` }}
    >
      <div className="sticky top-0 flex h-screen items-center">
        <div className="container mx-auto grid grid-cols-2 items-center gap-10 px-4">
          <NarrativeRail step={step} progress={scrollYProgress} />
          <FlowStage step={step} progress={scrollYProgress} />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stacked flow (mobile + reduced motion)                                     */
/* -------------------------------------------------------------------------- */

function CyclingPhone({
  feature,
  cycle,
}: {
  feature: FlowFeature;
  cycle: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-25% 0px -25% 0px" });
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!cycle || !inView) return;
    const id = setInterval(
      () => setI((prev) => (prev + 1) % feature.actions.length),
      1900,
    );
    return () => clearInterval(id);
  }, [cycle, inView, feature.actions.length]);

  const action = feature.actions[i];

  return (
    <div ref={ref} className="relative flex flex-col items-center">
      <div
        className="pointer-events-none absolute top-1/2 h-[360px] w-[360px] -translate-y-1/2 rounded-full opacity-50 blur-[90px]"
        style={{
          background: `radial-gradient(circle, ${feature.glow}, transparent 70%)`,
        }}
      />
      <PhoneFrame className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <PhoneScreen screen={action.screen} />
          </motion.div>
        </AnimatePresence>
      </PhoneFrame>

      {/* Step dots */}
      <div className="relative mt-5 flex items-center gap-1.5">
        {feature.actions.map((a, ai) => (
          <span
            key={a.labelKey}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              ai === i ? "w-5 bg-brand-700" : "w-1.5 bg-brand-300",
            )}
          />
        ))}
      </div>
      <p className="relative mt-2 text-sm font-semibold text-brand-700">
        {Language.getKey(action.labelKey)}
      </p>
    </div>
  );
}

function StackedFlow({
  className,
  cycle,
}: {
  className?: string;
  cycle: boolean;
}) {
  return (
    <div className={cn("container mx-auto px-4 pb-8 pt-16", className)}>
      <div className="space-y-24">
        {FEATURES.map((feature) => (
          <div key={feature.key} className="flex flex-col items-center">
            <BlurFade inView className="mb-8 max-w-md text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-900 text-white shadow-lg shadow-brand-500/30">
                  <feature.icon className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <div className="text-xs font-semibold uppercase tracking-wide text-brand-500">
                    {Language.getKey(feature.eyebrowKey)}
                  </div>
                  <div className="font-heading text-xl font-bold text-brand-900">
                    {Language.getKey(feature.titleKey)}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                {Language.getKey(feature.descriptionKey)}
              </p>
            </BlurFade>
            <BlurFade inView delay={0.1}>
              <CyclingPhone feature={feature} cycle={cycle} />
            </BlurFade>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Scroll-driven product showcase. On large screens a pinned phone mockup
 * steps through the full Linqx journey (10 in-app actions across 3 features)
 * as the user scrolls; on small screens / reduced-motion it falls back to a
 * stacked, auto-cycling layout.
 */
export function FlowShowcase() {
  return (
    <section
      id="how-it-works"
      className="relative overflow-x-clip bg-brand-50/40"
    >
      <FlowIntro />
      <DesktopFlow className="hidden lg:block" />
      <StackedFlow className="lg:hidden" cycle />
    </section>
  );
}
